// src/components/calendar/CalendarTab.jsx
// 캘린더 탭: 월별 예약 현황 + 투숙 중 일괄 발송 + 퇴실 일괄 발송 + 예약 취소/수정

import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { buildMessage, sendMessage } from '../../services/messageTemplates';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function CalendarTab() {
  const { state, logMessage, showToast, cancelReservation, updateReservation, updateCustomer, markCheckinSent, markCheckoutSent } = useApp();
  const { reservations, rooms, roomTemplates } = state;

  const today = todayStr();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [editingRes, setEditingRes] = useState(null); // 수정 중인 예약

  // ── 투숙 중 일괄 발송 (바텀 시트) ──
  const [bulkPanel, setBulkPanel] = useState(null);   // 'mannertime' | 'checkout' | null
  const [bulkTargets, setBulkTargets] = useState([]); // 선택된 예약 ID 목록
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [showAllBulk, setShowAllBulk] = useState(false);
  const BULK_PREVIEW = 4;

  // ── 이용 기록 패널 (퇴실 발송 직후) ──
  const [usagePanel, setUsagePanel] = useState(null); // null | 예약 배열
  const [usageRecord, setUsageRecord] = useState({}); // { [resId]: { bbq, firewood, pool } }

  const SERVICES = [
    { key: 'bbq',         icon: '🔥', label: '바베큐' },
    { key: 'firewood',    icon: '🪵', label: '장작' },
    { key: 'pool',        icon: '🌊', label: '온수풀' },
    { key: 'early_checkin', icon: '⏰', label: '얼리 체크인' },
  ];

  // 기본 메시지
  const BULK_DEFAULT_MSG = {
    mannertime:
`안녕하세요 고객님 🙂

편안한 투숙 되고 계신가요?

🔔 매너타임 안내
밤 10시 이후에는 음악, 대화 소리를
조금만 줄여주시면 감사하겠습니다.

주변 이웃분들을 위해 협조 부탁드립니다 🙏
나머지 일정도 즐겁게 보내세요!`,
    checkout:
`안녕하세요 고객님 🙂

오늘 퇴실 안내 드립니다.

⏰ 퇴실 시간: 오전 11시
🧹 퇴실 전 부탁드립니다
  · 음식물 쓰레기 봉투에 담아주세요
  · 창문·도어락 확인해 주세요

이용해주셔서 진심으로 감사합니다 😊
다음에 또 뵙겠습니다!`,
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // ─── 활성 예약 (취소 제외) - 캘린더 dot 및 통계에 사용 ───
  const activeReservations = useMemo(
    () => reservations.filter(r => r.status !== 'cancelled'),
    [reservations]
  );

  // ─── 캘린더 날짜 배열 생성 ───
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push(dateStr);
    }
    return days;
  }, [year, month]);

  // ─── 충돌 예약 감지 (룸별 인덱싱으로 불필요한 교차 비교 제거) ───
  const { conflictDates, conflictResIds } = useMemo(() => {
    const dates = new Set();
    const resIds = new Set();

    // 룸별 예약 그룹화 → 같은 룸 내에서만 비교
    const byRoom = new Map();
    for (const r of activeReservations) {
      if (!r.checkin || !r.checkout) continue;
      if (!byRoom.has(r.roomId)) byRoom.set(r.roomId, []);
      byRoom.get(r.roomId).push(r);
    }

    for (const roomRes of byRoom.values()) {
      for (let i = 0; i < roomRes.length; i++) {
        for (let j = i + 1; j < roomRes.length; j++) {
          const a = roomRes[i], b = roomRes[j];
          if (a.checkin < b.checkout && a.checkout > b.checkin) {
            dates.add(a.checkin);
            dates.add(b.checkin);
            resIds.add(a.id);
            resIds.add(b.id);
          }
        }
      }
    }
    return { conflictDates: dates, conflictResIds: resIds };
  }, [activeReservations]);

  // ─── 날짜별 예약 수 (취소 제외) ───
  const dateMap = useMemo(() => {
    const map = {};
    activeReservations.forEach(r => {
      if (r.checkin) {
        if (!map[r.checkin]) map[r.checkin] = { checkin: 0, checkout: 0 };
        map[r.checkin].checkin++;
      }
      if (r.checkout) {
        if (!map[r.checkout]) map[r.checkout] = { checkin: 0, checkout: 0 };
        map[r.checkout].checkout++;
      }
    });
    return map;
  }, [activeReservations]);

  // ─── 선택된 날짜의 예약 (취소된 예약도 표시하되 상태 구분) ───
  const selectedCheckins = reservations.filter(r => r.checkin === selectedDate);
  const selectedCheckouts = reservations.filter(r => r.checkout === selectedDate);

  // ─── 현재 투숙 중 (checkin ≤ 오늘 < checkout, 취소 제외) ───
  const currentGuests     = activeReservations.filter(r => r.checkin <= today && r.checkout > today);
  const todayCheckoutsAll = activeReservations.filter(r => r.checkout === today); // 오늘 퇴실 전체 (발송 여부 무관)

  function prevMonth() {
    setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }

  // ─── 투숙 중 일괄 발송: 패널 열기 ───
  function openBulkPanel(type) {
    const pool = type === 'mannertime' ? currentGuests : todayCheckoutsAll;
    if (pool.length === 0) {
      showToast(
        type === 'mannertime' ? '현재 투숙 중인 고객이 없습니다' : '오늘 퇴실 예정 고객이 없습니다',
        'error'
      );
      return;
    }
    setBulkPanel(type);
    setBulkTargets(pool.map(r => r.id));
    setBulkMessage(BULK_DEFAULT_MSG[type]);
    setShowAllBulk(false);
  }

  function closeBulkPanel() {
    setBulkPanel(null);
    setBulkTargets([]);
    setBulkMessage('');
    setShowAllBulk(false);
  }

  function toggleBulkTarget(id) {
    setBulkTargets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  // ─── 투숙 중 일괄 발송: 발송 실행 ───
  async function handleBulkSend() {
    const pool    = bulkPanel === 'mannertime' ? currentGuests : todayCheckoutsAll;
    const targets = pool.filter(r => bulkTargets.includes(r.id));
    if (targets.length === 0) { showToast('발송할 대상을 선택해주세요', 'error'); return; }

    const wasCheckout = bulkPanel === 'checkout';

    setBulkSending(true);
    let success = 0;

    for (const res of targets) {
      // 고객 이름 개인화
      const personalMsg = bulkMessage.replace('고객님', `${res.customerName} 고객님`);
      try {
        await sendMessage({
          phone: res.phone,
          templateId: bulkPanel,
          room: rooms.find(r => r.id === res.roomId),
          data: { name: res.customerName },
          rawText: personalMsg,
        });
        logMessage({
          customerId: res.customerId,
          customerName: res.customerName,
          phone: res.phone,
          templateId: bulkPanel,
          templateName: bulkPanel === 'mannertime' ? '매너타임 안내' : '체크아웃 안내',
          roomId: res.roomId,
          messageText: personalMsg,
        });
        // 발송 상태 업데이트
        if (bulkPanel === 'mannertime') markCheckinSent(res.id);
        if (bulkPanel === 'checkout') markCheckoutSent([res.id]);
        success++;
        await new Promise(r => setTimeout(r, 200));
      } catch {}
    }

    setBulkSending(false);
    closeBulkPanel();
    showToast(`${success}건 발송 완료!`);

    // 퇴실 발송 완료 후 → 이용 기록 패널 열기
    if (wasCheckout && targets.length > 0) {
      const initRecord = {};
      targets.forEach(r => { initRecord[r.id] = {}; });
      setUsageRecord(initRecord);
      setUsagePanel(targets);
    }
  }

  // ─── 이용 기록: 예/아니오 토글 ───
  function toggleUsage(resId, key, val) {
    setUsageRecord(prev => {
      const cur = prev[resId] || {};
      return { ...prev, [resId]: { ...cur, [key]: cur[key] === val ? null : val } };
    });
  }

  // ─── 이용 기록: 저장 ───
  function handleSaveUsage() {
    const checkoutDate = today;

    usagePanel.forEach(res => {
      const record = usageRecord[res.id] || {};
      const hasAny = Object.values(record).some(v => v === true);
      if (!hasAny) {
        console.log('[이용기록] 체크 항목 없음 — 건너뜀:', res.customerName);
        return;
      }

      // customerId → phone 순서로 고객 찾기
      const customer = state.customers.find(c =>
        (res.customerId && c.id === res.customerId) || c.phone === res.phone
      );
      if (!customer) {
        console.warn('[이용기록] 고객을 찾을 수 없음:', res.customerName, res.phone);
        return;
      }

      // 날짜 + 카운트 형태로 저장
      const updates = { id: customer.id };
      if (record.bbq === true) {
        updates.last_used_bbq = checkoutDate;
        updates.bbq_count = (customer.bbq_count || 0) + 1;
      }
      if (record.firewood === true) {
        updates.last_used_firewood = checkoutDate;
        updates.firewood_count = (customer.firewood_count || 0) + 1;
      }
      if (record.pool === true) {
        updates.last_used_pool = checkoutDate;
        updates.pool_count = (customer.pool_count || 0) + 1;
      }
      if (record.early_checkin === true) {
        updates.last_early_checkin = checkoutDate;
        updates.early_checkin_count = (customer.early_checkin_count || 0) + 1;
      }

      console.log('[이용기록] 고객 업데이트:', customer.name, updates);
      updateCustomer(updates);

      // 예약 문서에도 이용 항목 기록
      updateReservation({
        ...res,
        used_bbq:          record.bbq           === true,
        used_firewood:     record.firewood      === true,
        used_pool:         record.pool          === true,
        used_early_checkin: record.early_checkin === true,
      });
    });

    setUsagePanel(null);
    setUsageRecord({});
    showToast('이용 기록이 저장되었습니다 ✓');
  }

  // ─── 퇴실처리 (문자 발송 없이 이용 기록만) ───
  function handleCheckout(res) {
    const initRecord = {};
    initRecord[res.id] = {};
    setUsageRecord(initRecord);
    setUsagePanel([res]);
  }

  // ─── 예약 취소 ───
  function handleCancel(res) {
    if (res.status === 'cancelled') return; // 이미 취소됨 → 무시
    const confirmed = window.confirm(`${res.customerName}님의 예약을 취소하시겠습니까?\n\n체크인: ${formatDate(res.checkin)}\n체크아웃: ${formatDate(res.checkout)}\n객실: ${res.roomName}`);
    if (!confirmed) return;
    cancelReservation(res.id);
    showToast('예약이 취소되었고 캘린더에서도 삭제되었습니다', 'success');
  }

  // ─── 예약 수정 완료 ───
  function handleUpdate(updatedRes) {
    updateReservation(updatedRes);
    setEditingRes(null);
    showToast('예약이 변경되었고 캘린더에 반영되었습니다', 'success');
  }

  // ─── 수정 패널 ───
  if (editingRes) {
    return (
      <EditReservationPanel
        res={editingRes}
        rooms={rooms}
        reservations={reservations}
        onSave={handleUpdate}
        onClose={() => setEditingRes(null)}
      />
    );
  }

  return (
    <>
    <div className="tab-content">
      {/* ─── 오늘 현황 ─── */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" onClick={() => setSelectedDate(today)} style={{ cursor: 'pointer' }}>
          <div className="stat-value" style={{ color: 'var(--green)' }}>
            {activeReservations.filter(r => r.checkin === today).length}
          </div>
          <div className="stat-label">오늘 체크인</div>
        </div>
        <div className="stat-card" onClick={() => setSelectedDate(today)} style={{ cursor: 'pointer' }}>
          <div className="stat-value" style={{ color: 'var(--amber)' }}>
            {activeReservations.filter(r => r.checkout === today).length}
          </div>
          <div className="stat-label">오늘 체크아웃</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {activeReservations.filter(r => r.checkin <= today && r.checkout > today).length}
          </div>
          <div className="stat-label">현재 투숙</div>
        </div>
      </div>

      {/* ─── 투숙 중 일괄 발송 버튼 ─── */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>
          투숙 중 고객에게 보내기
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>

          {/* 매너타임 */}
          <button
            onClick={() => openBulkPanel('mannertime')}
            style={{
              background: currentGuests.length > 0 ? '#1e1b4b' : 'var(--bg)',
              border: `1.5px solid ${currentGuests.length > 0 ? '#4f46e5' : 'var(--border)'}`,
              borderRadius: 14, padding: '13px 10px',
              cursor: currentGuests.length > 0 ? 'pointer' : 'default',
              textAlign: 'center', opacity: currentGuests.length === 0 ? 0.45 : 1,
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 5 }}>🌙</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: currentGuests.length > 0 ? 'white' : 'var(--text-secondary)', marginBottom: 3 }}>
              매너타임 안내
            </div>
            <div style={{ fontSize: 11, color: currentGuests.length > 0 ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)' }}>
              {currentGuests.length > 0 ? `${currentGuests.length}명 투숙 중` : '투숙 중 없음'}
            </div>
          </button>

          {/* 오늘 퇴실 고객 안내 */}
          <button
            onClick={() => openBulkPanel('checkout')}
            style={{
              background: todayCheckoutsAll.length > 0 ? '#fffbeb' : 'var(--bg)',
              border: `1.5px solid ${todayCheckoutsAll.length > 0 ? 'var(--amber)' : 'var(--border)'}`,
              borderRadius: 14, padding: '13px 10px',
              cursor: todayCheckoutsAll.length > 0 ? 'pointer' : 'default',
              textAlign: 'center', opacity: todayCheckoutsAll.length === 0 ? 0.45 : 1,
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 5 }}>🚪</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: todayCheckoutsAll.length > 0 ? '#92400e' : 'var(--text-secondary)', marginBottom: 3 }}>
              오늘 퇴실 고객 안내
            </div>
            <div style={{ fontSize: 11, color: todayCheckoutsAll.length > 0 ? '#b45309' : 'var(--text-muted)' }}>
              {todayCheckoutsAll.length > 0 ? `오늘 ${todayCheckoutsAll.length}명 퇴실` : '오늘 퇴실 없음'}
            </div>
          </button>

        </div>
      </div>

      {/* ─── 월 네비게이션 ─── */}
      <div className="calendar-nav">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-month-label">{year}년 {month + 1}월</span>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      {/* ─── 캘린더 ─── */}
      <div className="calendar-grid">
        {WEEKDAYS.map(day => (
          <div key={day} className="cal-weekday" style={{ color: day === '일' ? 'var(--red)' : day === '토' ? 'var(--primary)' : 'var(--text-secondary)' }}>
            {day}
          </div>
        ))}

        {calendarDays.map((dateStr, idx) => {
          if (!dateStr) return <div key={`empty_${idx}`} />;

          const dayNum = parseInt(dateStr.split('-')[2]);
          const dayOfWeek = new Date(dateStr).getDay();
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const info = dateMap[dateStr];

          return (
            <div
              key={dateStr}
              className={`cal-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedDate(dateStr)}
            >
              <span className="cal-day-num" style={{
                color: dayOfWeek === 0 ? 'var(--red)' : dayOfWeek === 6 ? 'var(--primary)' : undefined,
              }}>
                {dayNum}
              </span>
              {(info || conflictDates.has(dateStr)) && (
                <div className="cal-dots">
                  {info?.checkin > 0 && (
                    <span className="cal-dot cal-dot-green" title={`체크인 ${info.checkin}건`}>
                      {info.checkin > 1 ? info.checkin : ''}
                    </span>
                  )}
                  {info?.checkout > 0 && (
                    <span className="cal-dot cal-dot-amber" title={`체크아웃 ${info.checkout}건`}>
                      {info.checkout > 1 ? info.checkout : ''}
                    </span>
                  )}
                  {conflictDates.has(dateStr) && (
                    <span className="cal-dot cal-dot-red" title="중복 예약 있음" />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── 범례 ─── */}
      <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span className="cal-dot cal-dot-green" /> 체크인
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span className="cal-dot cal-dot-amber" /> 체크아웃
        </div>
        {conflictDates.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#dc2626' }}>
            <span className="cal-dot cal-dot-red" /> 중복 예약
          </div>
        )}
      </div>

      {/* ─── 선택 날짜 상세 ─── */}
      <p className="section-title">
        {selectedDate === today ? '오늘' : formatDate(selectedDate)} 예약 현황
      </p>

      {selectedCheckins.length === 0 && selectedCheckouts.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 16px', fontSize: 14 }}>
          해당 날짜에 예약이 없습니다
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selectedCheckins.map(res => (
            <ReservationCard
              key={res.id}
              res={res}
              type="checkin"
              hasConflict={conflictResIds.has(res.id)}
              onCancel={handleCancel}
              onEdit={setEditingRes}
              onCheckout={handleCheckout}
            />
          ))}
          {selectedCheckouts.map(res => (
            <ReservationCard
              key={res.id}
              res={res}
              type="checkout"
              hasConflict={conflictResIds.has(res.id)}
              onCancel={handleCancel}
              onEdit={setEditingRes}
              onCheckout={handleCheckout}
            />
          ))}
        </div>
      )}
    </div>

    {/* ════ 투숙 중 일괄 발송 바텀 시트 ════ */}
    {bulkPanel && (() => {
      const pool        = bulkPanel === 'mannertime' ? currentGuests : todayCheckoutsAll;
      const visiblePool = showAllBulk ? pool : pool.slice(0, BULK_PREVIEW);
      const hiddenCount = pool.length - BULK_PREVIEW;
      const isMannertime = bulkPanel === 'mannertime';

      return (
        <>
          {/* 배경 딤 */}
          <div
            onClick={closeBulkPanel}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300 }}
          />

          {/* 시트 */}
          <div style={{
            position: 'fixed',
            bottom: 'var(--tabbar-h)',
            left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: 480,
            background: 'white',
            borderRadius: '22px 22px 0 0',
            zIndex: 301,
            maxHeight: '84dvh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -4px 30px rgba(0,0,0,0.18)',
          }}>

            {/* 드래그 핸들 */}
            <div style={{ padding: '12px 0 6px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <div style={{ width: 38, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
            </div>

            {/* 헤더 */}
            <div style={{
              padding: '4px 20px 14px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid var(--border-light)',
            }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
                  {isMannertime ? '🌙 매너타임 안내' : '🚪 체크아웃 안내'} 일괄 발송
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {isMannertime
                    ? `현재 투숙 중 ${pool.length}명`
                    : `오늘(${today}) 퇴실 예정 ${pool.length}명`}
                </div>
              </div>
              <button
                onClick={closeBulkPanel}
                style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'var(--bg)', border: 'none',
                  fontSize: 16, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-muted)',
                }}
              >✕</button>
            </div>

            {/* 대상 고객 (항상 표시) */}
            <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border-light)' }}>
              {/* 선택 현황 + 전체 선택 */}
              <div style={{
                padding: '10px 20px 8px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>
                  발송 대상{' '}
                  <span style={{ color: 'var(--green-dark)' }}>{bulkTargets.length}명</span>
                  <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>선택됨</span>
                </span>
                <button
                  onClick={() => setBulkTargets(
                    bulkTargets.length === pool.length ? [] : pool.map(r => r.id)
                  )}
                  style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 10px',
                    border: '1px solid var(--border)', borderRadius: 8,
                    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  {bulkTargets.length === pool.length ? '전체 해제' : `전체 선택 (${pool.length}명)`}
                </button>
              </div>

              {/* 고객 행 */}
              {visiblePool.map((res, idx) => {
                const isSelected = bulkTargets.includes(res.id);
                return (
                  <div
                    key={res.id}
                    onClick={() => toggleBulkTarget(res.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 20px',
                      borderTop: '1px solid var(--border-light)',
                      background: isSelected ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {/* 체크박스 */}
                    <div style={{
                      width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${isSelected ? 'var(--green)' : 'var(--border)'}`,
                      background: isSelected ? 'var(--green)' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.12s',
                    }}>
                      {isSelected && <span style={{ color: 'white', fontSize: 11, fontWeight: 700 }}>✓</span>}
                    </div>
                    {/* 아바타 */}
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: isMannertime ? '#ede9fe' : '#fffbeb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14,
                      color: isMannertime ? '#6d28d9' : '#92400e',
                    }}>
                      {res.customerName[0]}
                    </div>
                    {/* 정보 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{res.customerName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {res.roomName} · {res.guests}명
                        {!isMannertime && ` · 오늘(${today}) 퇴실`}
                      </div>
                    </div>
                    {/* 체크인~체크아웃 */}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right' }}>
                      <div>{res.checkin?.slice(5)} ~</div>
                      <div style={{ fontWeight: 700, color: isMannertime ? 'var(--text-secondary)' : 'var(--amber)' }}>
                        {res.checkout?.slice(5)}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* 더보기 / 접기 */}
              {pool.length > BULK_PREVIEW && (
                <button
                  onClick={() => setShowAllBulk(v => !v)}
                  style={{
                    width: '100%', background: '#fafafa',
                    border: 'none', borderTop: '1px solid var(--border-light)',
                    padding: '10px 0', fontSize: 12, fontWeight: 600,
                    color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  {showAllBulk ? '▲ 접기' : `▼ 더보기 (${hiddenCount}명 더)`}
                </button>
              )}
            </div>

            {/* 메시지 편집 (스크롤 영역) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: '10px 10px 0 0', padding: '9px 13px',
                display: 'flex', alignItems: 'center', gap: 7,
              }}>
                <span style={{ fontSize: 14 }}>✅</span>
                <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600, lineHeight: 1.5 }}>
                  자동 생성된 메시지 · 수정 없이 바로 보내셔도 됩니다
                </span>
              </div>
              <textarea
                className="input"
                value={bulkMessage}
                onChange={e => setBulkMessage(e.target.value)}
                style={{
                  minHeight: 180, resize: 'none',
                  fontSize: 13, lineHeight: 1.75, fontFamily: 'inherit',
                  borderRadius: '0 0 10px 10px', borderTop: 'none',
                }}
              />
            </div>

            {/* 발송 버튼 (항상 고정) */}
            <div style={{
              flexShrink: 0, padding: '12px 20px 16px',
              borderTop: '1px solid var(--border-light)', background: 'white',
            }}>
              <button
                className="btn btn-block"
                onClick={handleBulkSend}
                disabled={bulkSending || bulkTargets.length === 0 || !bulkMessage.trim()}
                style={{
                  padding: '15px 0', fontSize: 15, fontWeight: 800, borderRadius: 14,
                  background: isMannertime ? '#4f46e5' : 'var(--amber)',
                  color: 'white', border: 'none', cursor: 'pointer',
                  opacity: (bulkTargets.length === 0 || !bulkMessage.trim()) ? 0.45 : 1,
                  boxShadow: isMannertime
                    ? '0 3px 14px rgba(79,70,229,0.35)'
                    : '0 3px 14px rgba(245,158,11,0.35)',
                }}
              >
                {bulkSending
                  ? <><div className="spinner" /> 발송 중...</>
                  : `${isMannertime ? '🌙' : '🚪'} ${bulkTargets.length}명에게 발송`
                }
              </button>
            </div>

          </div>
        </>
      );
    })()}

    {/* ════ 이용 기록 바텀 시트 (퇴실 발송 직후) ════ */}
    {usagePanel && (
      <>
        {/* 배경 딤 */}
        <div
          onClick={() => { setUsagePanel(null); setUsageRecord({}); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300 }}
        />

        {/* 시트 */}
        <div style={{
          position: 'fixed',
          bottom: 'var(--tabbar-h)',
          left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 480,
          background: 'white',
          borderRadius: '22px 22px 0 0',
          zIndex: 301,
          maxHeight: '84dvh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 -4px 30px rgba(0,0,0,0.18)',
        }}>

          {/* 핸들 */}
          <div style={{ padding: '12px 0 6px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{ width: 38, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
          </div>

          {/* 헤더 */}
          <div style={{
            padding: '4px 20px 14px', flexShrink: 0,
            borderBottom: '1px solid var(--border-light)',
          }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 3 }}>
              📋 고객 이용 기록
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              탭으로 빠르게 기록 · 텍스트 입력 없음
            </div>
          </div>

          {/* 고객별 이용 기록 (스크롤) */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {usagePanel.map((res, idx) => {
              const rec = usageRecord[res.id] || {};
              return (
                <div
                  key={res.id}
                  style={{
                    padding: '14px 20px',
                    borderBottom: idx < usagePanel.length - 1 ? '1px solid var(--border-light)' : 'none',
                  }}
                >
                  {/* 고객 헤더 */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                      background: '#fffbeb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14, color: '#92400e',
                    }}>
                      {res.customerName[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{res.customerName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {res.roomName} · {res.guests}명
                      </div>
                    </div>
                  </div>

                  {/* 서비스 질문 행 */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {SERVICES.map(svc => {
                      const val = rec[svc.key];
                      return (
                        <div
                          key={svc.key}
                          style={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '9px 12px',
                            background: '#fafafa',
                            borderRadius: 10,
                            border: '1px solid var(--border-light)',
                          }}
                        >
                          {/* 서비스 라벨 */}
                          <span style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{svc.icon}</span>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{svc.label}</span>
                          </span>

                          {/* 예/아니오 버튼 */}
                          <div style={{ display: 'flex', gap: 6 }}>
                            {/* 예 */}
                            <button
                              onClick={() => toggleUsage(res.id, svc.key, true)}
                              style={{
                                width: 52, height: 36, borderRadius: 10,
                                border: `2px solid ${val === true ? 'var(--green)' : 'var(--border)'}`,
                                background: val === true ? 'var(--green)' : 'white',
                                color: val === true ? 'white' : 'var(--text-muted)',
                                fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', transition: 'all 0.12s',
                              }}
                            >
                              ✔ 예
                            </button>
                            {/* 아니오 */}
                            <button
                              onClick={() => toggleUsage(res.id, svc.key, false)}
                              style={{
                                width: 52, height: 36, borderRadius: 10,
                                border: `2px solid ${val === false ? '#f87171' : 'var(--border)'}`,
                                background: val === false ? '#fef2f2' : 'white',
                                color: val === false ? '#dc2626' : 'var(--text-muted)',
                                fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', transition: 'all 0.12s',
                              }}
                            >
                              ✖ 아니오
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 하단 버튼 (고정) */}
          <div style={{
            flexShrink: 0, padding: '12px 20px 16px',
            borderTop: '1px solid var(--border-light)', background: 'white',
            display: 'flex', gap: 8,
          }}>
            <button
              onClick={() => { setUsagePanel(null); setUsageRecord({}); }}
              style={{
                flex: 1, padding: '13px 0', fontSize: 14, fontWeight: 700,
                borderRadius: 12, border: '1.5px solid var(--border)',
                background: 'white', color: 'var(--text-secondary)', cursor: 'pointer',
              }}
            >
              건너뛰기
            </button>
            <button
              onClick={handleSaveUsage}
              style={{
                flex: 2, padding: '13px 0', fontSize: 15, fontWeight: 800,
                borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                color: 'white', cursor: 'pointer',
                boxShadow: '0 3px 14px rgba(34,197,94,0.35)',
              }}
            >
              저장 완료
            </button>
          </div>

        </div>
      </>
    )}

    </>
  );
}

// ─── 예약 카드 ───
function ReservationCard({ res, type, hasConflict, onCancel, onEdit, onCheckout }) {
  const isCheckin = type === 'checkin';
  const isCancelled = res.status === 'cancelled';
  const sent = isCheckin ? res.checkinSent : res.checkoutSent;

  return (
    <div className="card" style={{
      borderLeft: `4px solid ${isCancelled ? 'var(--text-muted)' : hasConflict ? '#dc2626' : isCheckin ? 'var(--green)' : 'var(--amber)'}`,
      opacity: isCancelled ? 0.65 : 1,
      background: hasConflict && !isCancelled ? '#fff9f9' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: isCancelled ? '#f3f4f6' : isCheckin ? 'var(--green-light)' : 'var(--amber-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, flexShrink: 0,
        }}>
          {isCancelled ? '✕' : isCheckin ? '🏠' : '👋'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{res.customerName}</span>
            <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 10,
              background: isCancelled ? '#f3f4f6' : isCheckin ? 'var(--green-light)' : 'var(--amber-light)',
              color: isCancelled ? 'var(--text-muted)' : isCheckin ? 'var(--green-dark)' : '#92400e',
              fontWeight: 600 }}>
              {isCancelled ? '취소됨' : isCheckin ? '체크인' : '체크아웃'}
            </span>
            {hasConflict && !isCancelled && (
              <span style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 10,
                background: '#fef2f2', color: '#dc2626', fontWeight: 700,
                border: '1px solid #fca5a5',
              }}>
                ⚠ 중복
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {res.roomName} · {res.guests}명 · {res.channel}
          </div>
        </div>
        {!isCancelled && (
          <span style={{
            fontSize: 11, padding: '3px 8px', borderRadius: 20,
            background: sent ? 'var(--green-light)' : '#fef3c7',
            color: sent ? 'var(--green-dark)' : '#92400e',
            fontWeight: 600, flexShrink: 0,
          }}>
            {sent ? '발송완료' : '미발송'}
          </span>
        )}
      </div>

      {/* ─── 액션 버튼 (취소되지 않은 예약만) ─── */}
      {!isCancelled && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={() => onEdit(res)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'white',
              color: 'var(--text-secondary)', cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            수정
          </button>
          <button
            onClick={() => onCheckout(res)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 8,
              border: '1px solid #a7f3d0', background: '#ecfdf5',
              color: '#065f46', cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            퇴실처리
          </button>
          <button
            onClick={() => onCancel(res)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 8,
              border: '1px solid #fca5a5', background: '#fef2f2',
              color: '#dc2626', cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            예약 취소
          </button>
        </div>
      )}
    </div>
  );
}

// ─── 예약 수정 패널 ───
function EditReservationPanel({ res, rooms, reservations, onSave, onClose }) {
  const [form, setForm] = useState({
    customerName: res.customerName || '',
    phone: res.phone || '',
    checkin: res.checkin || '',
    checkout: res.checkout || '',
    guests: String(res.guests || ''),
    roomId: res.roomId || '',
    roomName: res.roomName || '',
    channel: res.channel || '',
  });
  const [conflictDialog, setConflictDialog] = useState(null);

  function setField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function selectRoom(room) {
    setForm(prev => ({ ...prev, roomId: room.id, roomName: room.name }));
  }

  function checkConflict() {
    if (!form.roomId || !form.checkin || !form.checkout) return [];
    return (reservations || []).filter(r =>
      r.id !== res.id &&
      r.status !== 'cancelled' &&
      r.roomId === form.roomId &&
      r.checkin && r.checkout &&
      form.checkin < r.checkout &&
      form.checkout > r.checkin
    );
  }

  function handleSave() {
    if (!form.checkin || !form.checkout || !form.roomId) return;
    const conflicts = checkConflict();
    if (conflicts.length > 0) {
      setConflictDialog({ conflicts, onConfirm: () => { setConflictDialog(null); doSave(); } });
      return;
    }
    doSave();
  }

  function doSave() {
    onSave({ ...res, ...form, guests: parseInt(form.guests) || 0 });
  }

  const isValid = form.customerName && form.checkin && form.checkout && form.roomId;

  return (
    <div className="tab-content">
      {/* ─── 중복 예약 경고 다이얼로그 ─── */}
      {conflictDialog && (
        <>
          <div
            onClick={() => setConflictDialog(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }}
          />
          <div style={{
            position: 'fixed',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)',
            maxWidth: 340,
            background: 'white',
            borderRadius: 20,
            zIndex: 401,
            padding: '24px 20px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 16, textAlign: 'center', marginBottom: 6 }}>
              같은 객실에 이미 예약이 있습니다
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 14 }}>
              날짜가 겹치는 기존 예약을 확인하세요
            </div>
            {conflictDialog.conflicts.map(r => (
              <div key={r.id} style={{
                background: '#fef2f2', borderRadius: 10,
                padding: '10px 14px', marginBottom: 8,
                border: '1px solid #fca5a5',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }}>{r.roomName}</div>
                <div style={{ fontSize: 12, color: '#991b1b', marginTop: 3 }}>
                  {r.customerName} · {r.checkin} ~ {r.checkout}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setConflictDialog(null)}
                style={{
                  flex: 1, padding: '12px', borderRadius: 12,
                  border: '1px solid var(--border)', background: 'white',
                  fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={conflictDialog.onConfirm}
                style={{
                  flex: 1, padding: '12px', borderRadius: 12,
                  border: 'none', background: '#dc2626',
                  fontSize: 14, fontWeight: 700, color: 'white', cursor: 'pointer',
                }}
              >
                그래도 저장
              </button>
            </div>
          </div>
        </>
      )}

      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}
        >
          ‹
        </button>
        <div>
          <p className="section-title" style={{ margin: 0 }}>예약 수정</p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            수정 후 저장하면 캘린더에 즉시 반영됩니다
          </p>
        </div>
      </div>

      {/* 고객 정보 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: 'var(--text-secondary)' }}>고객 정보</p>
        <div className="input-group">
          <label className="input-label">이름</label>
          <input
            className="input"
            value={form.customerName}
            onChange={e => setField('customerName', e.target.value)}
            placeholder="예약자명"
          />
        </div>
        <div className="input-group">
          <label className="input-label">전화번호</label>
          <input
            className="input"
            type="tel"
            value={form.phone}
            onChange={e => setField('phone', e.target.value)}
            placeholder="010-0000-0000"
          />
        </div>
        <div className="input-group">
          <label className="input-label">인원</label>
          <input
            className="input"
            type="number"
            value={form.guests}
            onChange={e => setField('guests', e.target.value)}
            placeholder="명"
          />
        </div>
      </div>

      {/* 날짜 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: 'var(--text-secondary)' }}>예약 날짜</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="input-group">
            <label className="input-label">체크인</label>
            <input
              className="input"
              type="date"
              value={form.checkin}
              onChange={e => setField('checkin', e.target.value)}
            />
          </div>
          <div className="input-group">
            <label className="input-label">체크아웃</label>
            <input
              className="input"
              type="date"
              value={form.checkout}
              onChange={e => setField('checkout', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 객실 선택 */}
      <p className="section-title">객실 선택</p>
      <div className="room-grid" style={{ marginBottom: 16 }}>
        {rooms.map(room => (
          <div
            key={room.id}
            className={`room-card ${form.roomId === room.id ? 'selected' : ''}`}
            onClick={() => selectRoom(room)}
          >
            <div className="room-name">{room.name}</div>
            <div className="room-capacity">최대 {room.capacity}명</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {room.checkinTime} → {room.checkoutTime}
            </div>
          </div>
        ))}
      </div>

      {/* 변경 내역 미리보기 */}
      {(form.checkin !== res.checkin || form.checkout !== res.checkout || form.roomId !== res.roomId) && (
        <div className="card" style={{ marginBottom: 16, background: '#fffbeb', border: '1px solid #fde68a' }}>
          <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#92400e' }}>변경 내역</p>
          {(form.checkin !== res.checkin || form.checkout !== res.checkout) && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
              <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>
                {formatDate(res.checkin)} ~ {formatDate(res.checkout)}
              </span>
              {' → '}
              <span style={{ color: 'var(--green-dark)', fontWeight: 600 }}>
                {formatDate(form.checkin)} ~ {formatDate(form.checkout)}
              </span>
            </div>
          )}
          {form.roomId !== res.roomId && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{res.roomName}</span>
              {' → '}
              <span style={{ color: 'var(--green-dark)', fontWeight: 600 }}>{form.roomName}</span>
            </div>
          )}
        </div>
      )}

      {/* 저장 버튼 */}
      <button
        className="btn btn-green btn-block"
        style={{ padding: 16, fontSize: 16 }}
        onClick={handleSave}
        disabled={!isValid}
      >
        저장하고 캘린더 반영
      </button>

      {!isValid && (
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          이름, 날짜, 객실을 모두 입력해주세요
        </p>
      )}
    </div>
  );
}
